import { useQuery, UseQueryOptions } from "react-query";
import ORM, { BaseModel } from "./orm";
import { DataWhitelist, FilterConfig } from "./pipelineNode";
import ApiService, { SingleRecordResponse } from "@services/api/api.service";
import { BuildOrchestration } from "./buildOrchestration";
import { DashboardDateRange } from "./dashboard";


export interface AnalysisDimensionFilter {
    dimension_id: string;
    comparator: string;
    value: string|string[];
}

export interface AnalysisSortOrder {
    // This will be "METRIC" or "DIMENSION"
    entity_type: string;
    entity_id: string;
    ascending: boolean;
}

export interface Analysis extends BaseModel {
    name: string;
    visualization_type: string;
    description: string;
    data_source_type: 'DYNAMIC' | 'STATIC';
    viz_options: any;
    dimension_ids: string[];
    measure_ids: string[];
    dimension_filters?: AnalysisDimensionFilter[];
    date_range?: string;
    custom_date_range_start?: string;
    custom_date_range_end?: string;
    user_timezone?: string;
    ephemeral: boolean;
    default_sort_order?: AnalysisSortOrder[] | null;
    ignore_empty_dimensions?: boolean;
    skip_null_values?: boolean;
    advanced?: boolean;
    compare_to_previous_period?: boolean;
    
    // New static analysis fields
    static_source_pipeline_node_id?: string;
    static_date_dimension_field_id?: string;

    custom_parameters?: any;
}



export interface AnalysisVisualizationConfig {
    render_type: string;
    options: any;
}

export interface AnalysisConfigurationParams {
    analysis?: Analysis;
    sortOrder?: AnalysisSortOrder[] | null;
    page?: number;
    perPage?: number;
    filters?: FilterConfig;
    dateRange?: DashboardDateRange;
    dimensionFilters?: AnalysisDimensionFilter[];
    customParameters?: any;
}

interface VizConfigResponse extends SingleRecordResponse<AnalysisVisualizationConfig> {
    node_id: string;
}

export class _AnalysisORM extends ORM<Analysis>{
    public async prepAnalyses(
        analyses: Analysis[],
        dimensionFilters: AnalysisDimensionFilter[] = [],
        dateRangeConfig?: DashboardDateRange,
        skipDateRangeAnalysisIds?: string[],
        customParameters?: any

    ): Promise<BuildOrchestration> {
        const result = await ApiService.getInstance().request('POST', '/analyses/prep', {
            'analyses': analyses,
            'dimension_filters': dimensionFilters,
            'date_range_config': dateRangeConfig,
            'skip_date_range_analysis_ids': skipDateRangeAnalysisIds,
            'custom_parameters': customParameters,
        }) as SingleRecordResponse<BuildOrchestration>;
        return result.record;
    }

    public async getVisualizationConfig(params: AnalysisConfigurationParams): Promise<VizConfigResponse> {
        const { analysis, sortOrder = [], page = 1, perPage = 3, filters, dateRange, dimensionFilters, customParameters } = params;

        let sortArg = null;
        if (sortOrder && sortOrder.length > 0) {
            sortArg = sortOrder;
        }

        let filterArg = null;
        if (filters && filters.filters.length > 0) {
            filterArg = filters;
        }

        const requestParams: Record<string, any> = {
            'sort': sortArg,
            'page': page,
            'page_size': perPage,
            'filters': filterArg,
            'analysis': analysis,
            'date_range_config': dateRange,
            'dimension_filters': dimensionFilters,
            'custom_parameters': customParameters
        };


        const result = await ApiService.getInstance().request('POST', `/${this.endpoint}/data`, requestParams) as VizConfigResponse;
        return result;
    }
};

export const AnalysisORM = new _AnalysisORM('analyses');

export const useAnalyses = () => {
    return useQuery(['analyses'], async () => {
        const result = await AnalysisORM.find({
            ephemeral: {
                '$ne': true
            }
        });
        return result.records;
    });
}

export const useAnalysis = (analysisId: string) => {
    return useQuery(['analyses', analysisId], async () => {
        if (!analysisId) {
            return undefined;
        }
        const result = await AnalysisORM.findById(analysisId);
        return result;
    })
}

export const useAnalysisConfigurationData = (
    params: AnalysisConfigurationParams,
    options?: UseQueryOptions<VizConfigResponse>
) => {
    const { analysis, sortOrder, page, perPage, dateRange, dimensionFilters, customParameters } = params;

    return useQuery<VizConfigResponse>({
        queryKey: [
            'analysisConfiguration',
            analysis?.visualization_type,
            analysis?.measure_ids,
            analysis?.dimension_ids,
            analysis?.dimension_filters ? JSON.stringify(analysis.dimension_filters) : null,
            analysis?.custom_parameters ? JSON.stringify(analysis.custom_parameters) : null,
            sortOrder ? JSON.stringify(sortOrder) : null,
            page,
            perPage,
            params.filters ? JSON.stringify(params.filters) : null,
            dateRange ? JSON.stringify(dateRange) : null,
            dimensionFilters ? JSON.stringify(dimensionFilters) : null,
            customParameters ? JSON.stringify(customParameters) : null,
        ],
        queryFn: () => {
            if (!analysis) {
                return Promise.reject(new Error('Analysis is required'));
            }
            return AnalysisORM.getVisualizationConfig(params);
        },
        enabled: !!analysis && (options?.enabled !== false),
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: (failureCount, error: any) => {
            if (error?.code === 404) {
                return false;
            }
            return failureCount < 3;
        },
        ...options,
    });
};
